/**
 * Calculates the total number of pages based on the number of items per page and the total number of items.
 *
 * @param {number} itemsPerPage - The number of items to display per page.
 * @param {number} totalItems - The total number of items.
 * @returns {number} The total number of pages.
 *
 * @example
 * // Returns 4
 * calculateTotalPages(10, 36);
 *
 * @example
 * // Returns 1
 * calculateTotalPages(50, 47);
 */
export function calculateTotalPages(itemsPerPage, totalItems) {
    return Math.ceil(totalItems / itemsPerPage);
}

/**
 * Checks if an object has no properties.
 * @param {object} obj The object to check.
 * @returns `true` if the object has no properties and `false` otherwise.
 */
export function isObjectEmpty(obj) {
    if (obj === undefined || obj === null) {
        return true; // Treat non-object values or null as empty.
    }

    return Object.keys(obj).length === 0;
}

/**
 * @note The file object provided by browsers is not the same as the file object in a Node.js backend. Therefore,
 * this handling code can't be shared between the frontend and backend.
 *
 * @param {File} file
 * @returns {Promise<string>} Data URL for the file (https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/Data_URLs).
 */
export function getFileDataURL(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            resolve(reader.result);
        };
        reader.onerror = () => {
            reject("Failed to load file");
        };

        reader.readAsDataURL(file);
    });
}

/**
 * Removes the anchor link (hash fragment) from a given URL.
 *
 * IMPORTANT: This function will remove part of a query or URL parameter if the
 * parameter contains a `#` and the URL does not have an anchor link.
 *
 * @param {string} url The URL to remove the anchor link from.
 * @returns {string} The URL without the anchor link.
 */
export function removeAnchorLink(url) {
    const lastHashIndex = url.lastIndexOf("#");

    return lastHashIndex === -1 ? url : url.substring(0, lastHashIndex);
}
